import {
  Component,
  computed,
  EventEmitter,
  Output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { OfferPaymentAgreementNode } from '@modules/campaigns/Domain/Nodes/OfferPaymentAgreement';
import { OfferPaymentAgreementConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { NodeValidationError } from '@modules/campaigns/Domain/Interfaces/NodeValidationError';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { INodeInspectorAdapter } from '@shared/components/joint-js/services/INodeInspectorAdapter';
import { InspectorMode } from '@shared/components/joint-js/components/inspectors/node-inspector/node-inspector.component';
import { DividerModule } from 'primeng/divider';
import { PipeModule } from '@shared/pipes/pipe.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessagesModule } from 'primeng/messages';

type ExpirationMode = 'days';

@Component({
  selector: 'app-offer-payment-agreement-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    DividerModule,
    PipeModule,
    TranslateModule,
    SelectButtonModule,
    FormsModule,
    InputNumberModule,
    MessagesModule,
  ],
  templateUrl: './offer-payment-agreement-inspector.component.html',
  styleUrls: ['./offer-payment-agreement-inspector.component.scss'],
})
export class OfferPaymentAgreementInspectorComponent implements INodeInspectorAdapter {
  @Output() closeSidebar = new EventEmitter<void>();

  node: WritableSignal<NodeClass | null> = signal<NodeClass | null>(null);

  activeTab = signal<'preview' | 'config'>('preview');
  mode = signal<InspectorMode>('edit');

  expirationMode = signal<ExpirationMode>('days');
  expirationDays = signal<number | null>(1);
  numberOfInstalments = signal<number | null>(2);

  errors = signal<NodeValidationError[]>([]);

  private changeTimeout?: ReturnType<typeof setTimeout>;

  readonly tabs = computed(() => [
    {
      label: this.translate.instant('DATA.MODULES.INSPECTORS.OFFER_PAYMENT_AGREEMENT.TABS.PREVIEW'),
      value: 'preview' as const,
    },
    {
      label: this.translate.instant('DATA.MODULES.INSPECTORS.OFFER_PAYMENT_AGREEMENT.TABS.CONFIG'),
      value: 'config' as const,
    },
  ]);

  readonly visibleTabs = computed(() =>
    this.mode() === 'edit' ? this.tabs() : this.tabs().filter((t) => t.value === 'preview'),
  );

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
  ) {}

  get nodeValue(): OfferPaymentAgreementNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.OfferPaymentAgreement
      ? (current as OfferPaymentAgreementNode)
      : null;
  }

  get previewExpirationText(): string {
    const days = this.expirationDays();
    if (days == null) return '-';

    return `${days} ${
      days === 1
        ? this.translate.instant('DATA.COMMON.TIME.DAY')
        : this.translate.instant('DATA.COMMON.TIME.DAYS')
    }`;
  }

  get previewInstalmentsText(): string {
    const instalments = this.numberOfInstalments();
    if (instalments == null) return '-';
    return `${instalments} ${
      instalments === 1
        ? this.translate.instant('DATA.MODULES.CAMPAINGS.ELEMENTS.NODES.PARAMETERS.FEE')
        : this.translate.instant('DATA.MODULES.CAMPAINGS.ELEMENTS.NODES.PARAMETERS.INSTALLMENTS')
    }`;
  }

  setNode(nodeSignal: Signal<NodeClass | null>) {
    this.node.set(nodeSignal());
    this.refreshFromConfig();
  }

  setMode(mode: InspectorMode) {
    this.mode.set(mode);
    if (mode === 'preview') {
      this.activeTab.set('preview');
    }
  }

  setActiveTab(tab: 'preview' | 'config') {
    this.activeTab.set(tab);
  }

  applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const updatedConfig: OfferPaymentAgreementConfig = {
      ...(node.config as OfferPaymentAgreementConfig),
      expirationDays: this.expirationDays(),
      numberOfInstalments: this.numberOfInstalments(),
    };

    const updated = node.cloneWithConfig(updatedConfig) as OfferPaymentAgreementNode;
    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  onExpirationDaysChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 0;
    if (value < 0) value = 0;
    if (value > 99) value = 99;

    this.expirationDays.set(value);
    this.expirationMode.set('days');
    this.scheduleApplyChanges();
  }

  onInstalmentsChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 2;
    if (value < 1) value = 1;
    if (value > 11) value = 11;

    this.numberOfInstalments.set(value);
    this.scheduleApplyChanges();
  }

  private scheduleApplyChanges() {
    if (this.changeTimeout) clearTimeout(this.changeTimeout);
    this.changeTimeout = setTimeout(() => this.applyChanges(), 500);
  }

  private refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as OfferPaymentAgreementConfig;

    this.expirationDays.set(cfg.expirationDays ?? 1);
    this.numberOfInstalments.set(cfg.numberOfInstalments ?? 2);

    this.expirationMode.set('days');
    this.errors.set([]);
  }
}
